import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AdminBillingAnomaly, BillingAnomalyResolvedFilter } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useBillingAnomaliesList } from '../api/useAdmin.ts';
import { CustomerRef } from '../components/identity/CustomerRef.tsx';
import { formatLocalTimestamp, formatUsdMicrosOrCents } from '../components/formatters.ts';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import {
  Button,
  EmptyState,
  PageHeader,
  TBody,
  Table,
  TableSkeleton,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

const FILTER_TABS: readonly {
  readonly value: BillingAnomalyResolvedFilter;
  readonly labelKey: I18nKey;
  readonly hintKey: I18nKey;
}[] = [
  {
    value: 'unresolved',
    labelKey: 'billing_anomalies.filter.unresolved',
    hintKey: 'billing_anomalies.filter.unresolved.hint',
  },
  {
    value: 'resolved',
    labelKey: 'billing_anomalies.filter.resolved',
    hintKey: 'billing_anomalies.filter.resolved.hint',
  },
  {
    value: 'all',
    labelKey: 'billing_anomalies.filter.all',
    hintKey: 'billing_anomalies.filter.all.hint',
  },
] as const;

const PAGE_LIMIT = 50;

function truncateId(value: string, head = 8, tail = 4): string {
  if (value.length <= head + tail + 3) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

function BillingAnomalyRow({ row }: { readonly row: AdminBillingAnomaly }): JSX.Element {
  return (
    <Tr>
      <Td mono>
        <Link
          to={`/customers/accounts/${encodeURIComponent(row.account_id)}`}
          title={row.account_id}
          className="text-paper-mid hover:text-signal"
        >
          <CustomerRef accountId={row.account_id} />
        </Link>
      </Td>
      <Td mono title={row.request_id}>
        {truncateId(row.request_id)}
      </Td>
      <Td emphasis>
        {row.vendor} / {row.model}
      </Td>
      <Td num emphasis>
        {formatUsdMicrosOrCents(row.amount_usd_micros, row.amount_usd_cents, false)}
      </Td>
      <Td emphasis>{row.reason}</Td>
      <Td>{row.last_replay_status ?? '—'}</Td>
      <Td mono>{formatLocalTimestamp(row.created_at, { seconds: true })}</Td>
      <Td mono>
        {row.resolved_at === null ? '—' : formatLocalTimestamp(row.resolved_at, { seconds: true })}
      </Td>
    </Tr>
  );
}

export function BillingAnomalies(): JSX.Element {
  const t = useT();
  const [filter, setFilter] = useState<BillingAnomalyResolvedFilter>('unresolved');
  const [offset, setOffset] = useState(0);
  const query = useBillingAnomaliesList(filter, { limit: PAGE_LIMIT, offset });

  const total = query.data?.total ?? 0;
  const currentOffset = query.data?.offset ?? offset;
  const currentLimit = query.data?.limit ?? PAGE_LIMIT;
  const rows = useMemo(() => query.data?.billing_anomalies ?? [], [query.data]);
  const hasPrev = currentOffset > 0;
  const hasNext = currentOffset + currentLimit < total;

  function handleFilterChange(next: BillingAnomalyResolvedFilter): void {
    setFilter(next);
    setOffset(0);
  }

  return (
    <section data-testid="billing-anomalies-page" className="flex flex-col gap-8">
      <PageHeader title={t('billing_anomalies.title')} sub={t('billing_anomalies.subtitle')} />

      <nav aria-label={t('billing_anomalies.title')} className="flex flex-wrap items-center gap-2">
        {FILTER_TABS.map((tab) => {
          const isActive = tab.value === filter;
          return (
            <Button
              key={tab.value}
              aria-pressed={isActive}
              onClick={() => handleFilterChange(tab.value)}
              title={t(tab.hintKey)}
              className={isActive ? 'bg-active text-paper' : undefined}
            >
              {t(tab.labelKey)}
            </Button>
          );
        })}
      </nav>

      {query.isError ? (
        <div
          role="alert"
          className="flex items-center justify-between gap-4 rounded-lg border border-bad-line bg-bad-dim/50 px-4 py-3"
        >
          <div className="flex flex-col gap-1">
            <span className="text-body font-medium text-bad">{t('billing_anomalies.error.title')}</span>
            <span className="text-dense text-bad-bright">
              {query.error instanceof OpsApiError
                ? `${query.error.code}: ${query.error.message}`
                : t('common.network_error')}
            </span>
          </div>
          <Button onClick={() => void query.refetch()}>{t('common.retry')}</Button>
        </div>
      ) : null}

      {query.isPending ? <TableSkeleton rows={8} /> : null}

      {!query.isPending && !query.isError && rows.length === 0 ? (
        <div className="rounded-lg border border-line-soft bg-card">
          <EmptyState
            title={
              filter === 'unresolved'
                ? t('billing_anomalies.empty.unresolved')
                : filter === 'resolved'
                  ? t('billing_anomalies.empty.resolved')
                  : t('billing_anomalies.empty.all')
            }
            hint={t('billing_anomalies.empty.hint')}
          />
        </div>
      ) : null}

      {!query.isPending && !query.isError && rows.length > 0 ? (
        <div className="flex flex-col gap-2">
          <Table>
            <THead>
              <Tr interactive={false}>
                <Th>{t('billing_anomalies.column.account')}</Th>
                <Th>{t('billing_anomalies.column.request_id')}</Th>
                <Th>{t('billing_anomalies.column.model')}</Th>
                <Th num>{t('billing_anomalies.column.amount')}</Th>
                <Th>{t('billing_anomalies.column.reason')}</Th>
                <Th>{t('billing_anomalies.column.replay')}</Th>
                <Th>{t('billing_anomalies.column.created')}</Th>
                <Th>{t('billing_anomalies.column.resolved')}</Th>
              </Tr>
            </THead>
            <TBody>
              {rows.map((row) => (
                <BillingAnomalyRow key={row.id} row={row} />
              ))}
            </TBody>
          </Table>
          <div className="flex items-center justify-between px-1 text-dense text-paper-low">
            <span>{t('billing_anomalies.pagination.summary', { shown: rows.length, total })}</span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => setOffset((prev) => Math.max(0, prev - PAGE_LIMIT))}
                disabled={!hasPrev}
              >
                {t('common.previous')}
              </Button>
              <Button
                size="sm"
                onClick={() => setOffset((prev) => prev + PAGE_LIMIT)}
                disabled={!hasNext}
              >
                {t('common.next')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
